import { Autocomplete, Icon } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface Option {
  label: string;
  value: string;
}

interface Props {
  label: string;
  options: Option[];
  selected: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

// Searchable replacement for <Select> — the language list is long (~138 entries), so let the merchant
// type to filter instead of scrolling.
const LanguageAutocomplete = ({ label, options, selected, onChange, placeholder = 'Search languages', disabled = false }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [filtered, setFiltered] = useState<Option[]>(options);

  const selectedLabel = useMemo(() => options.find((o) => o.value === selected)?.label ?? '', [options, selected]);

  // Reflect the current selection (and reset on close) in the input text.
  useEffect(() => setInputValue(selectedLabel), [selectedLabel]);
  // Keep the option list in sync when the source options change (e.g. after a language is added).
  useEffect(() => setFiltered(options), [options]);

  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);
      const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(escaped, 'i');
      setFiltered(value ? options.filter((o) => re.test(o.label)) : options);
    },
    [options],
  );

  const updateSelection = useCallback((sel: string[]) => onChange(sel[0] ?? ''), [onChange]);

  return (
    <Autocomplete
      options={filtered}
      selected={selected ? [selected] : []}
      onSelect={updateSelection}
      textField={
        <Autocomplete.TextField
          label={label}
          value={inputValue}
          onChange={updateText}
          prefix={<Icon source={SearchIcon} />}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
        />
      }
    />
  );
};

export default LanguageAutocomplete;
